import React from 'react';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  BadgeCell,
  BadgeCellComponentProps,
  DropdownFilterOption,
  FilterItems,
  IconTextCell,
  IconTextCellComponentProps,
  IconTextCellSkeleton,
} from '@odin-labs/components';
import {
  ensureNonEmptyItems,
  getInitialsForUser,
  getOrganizationName,
  getUserFullName,
} from 'utils';
import {
  AttendanceColumn,
  AttendanceColumnName,
  ScheduleAttendanceEntry,
} from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import { WrenchIcon } from 'components/icons';
import { ScheduleAttendanceStatus } from 'apollo/generated/client-operations';
import { ScheduleAttendanceFilters } from './types';

type WorkerCellProps = AvatarCellComponentProps | { missing: boolean };

const statusTextMap: Record<ScheduleAttendanceStatus, string> = {
  [ScheduleAttendanceStatus.Absent]: 'Absent',
  [ScheduleAttendanceStatus.Present]: 'Present',
  [ScheduleAttendanceStatus.Scheduled]: 'Scheduled',
  [ScheduleAttendanceStatus.Unscheduled]: 'Unscheduled',
};
const statusColorMap: Record<
  ScheduleAttendanceStatus,
  BadgeCellComponentProps['color']
> = {
  [ScheduleAttendanceStatus.Absent]: 'red',
  [ScheduleAttendanceStatus.Present]: 'green',
  [ScheduleAttendanceStatus.Scheduled]: 'gray',
  [ScheduleAttendanceStatus.Unscheduled]: 'yellow',
};

const attendanceStatusesOptions: DropdownFilterOption[] = [
  { value: ScheduleAttendanceStatus.Absent, label: 'Absent' },
  { value: ScheduleAttendanceStatus.Present, label: 'Present' },
  { value: ScheduleAttendanceStatus.Scheduled, label: 'Scheduled' },
  { value: ScheduleAttendanceStatus.Unscheduled, label: 'Unscheduled' },
];

export const getColumns = (): AttendanceColumn[] =>
  ensureNonEmptyItems<AttendanceColumn>([
    {
      id: AttendanceColumnName.Contractor,
      Header: 'Contractor',
      accessor: (ae: ScheduleAttendanceEntry): string => {
        return getOrganizationName(
          ae?.jobsiteContractor?.contractor?.organization,
        );
      },
      Cell: IconTextCell,
      CellLoading: IconTextCellSkeleton,
      componentProps: (): IconTextCellComponentProps => ({
        icon: WrenchIcon,
        size: 'md',
      }),
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.Status,
      Header: 'Status',
      accessor: (ae: ScheduleAttendanceEntry): string => {
        return ae?.status;
      },
      Cell: BadgeCell,
      componentProps: (ae: ScheduleAttendanceEntry): BadgeCellComponentProps =>
        ae
          ? {
              color: statusColorMap[ae.status],
              text: statusTextMap[ae.status],
              size: 'lg',
            }
          : {
              color: 'gray',
              text: 'Unknown',
              size: 'lg',
            },
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.Worker,
      Header: 'Worker',
      accessor: (ae: ScheduleAttendanceEntry): string => {
        const { user } = ae?.jobsiteWorker?.contractorWorker?.worker ?? {};
        return getUserFullName(user);
      },
      Cell: (props: { componentProps: WorkerCellProps }): React.ReactNode => {
        const { componentProps } = props;
        if ('missing' in componentProps && componentProps.missing) {
          return <BadgeCell componentProps={{ text: 'TBD', color: 'gray' }} />;
        }
        return <AvatarCell {...props} />;
      },
      CellLoading: <AvatarSkeleton size="xs" hideDetails />,
      componentProps: (ae: ScheduleAttendanceEntry): WorkerCellProps => {
        const { worker } = ae?.jobsiteWorker?.contractorWorker ?? {};

        return worker
          ? {
              src: worker.profilePictureCropped?.downloadUrl,
              placeholder: getInitialsForUser(worker.user),
              objectFit: 'cover',
              size: 'xs',
            }
          : { missing: true };
      },
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.Trade,
      Header: 'Trade',
      accessor: (ae: ScheduleAttendanceEntry): string => {
        return ae?.jobsiteWorker?.trade ?? ae?.scheduleWorker?.trade ?? '';
      },
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.Activity,
      Header: 'Activity',
      accessor: (ae: ScheduleAttendanceEntry): string => {
        return ae?.scheduleWorker?.activity ?? '';
      },
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.Start,
      Header: 'Start',
      accessor: (ae: ScheduleAttendanceEntry) =>
        ae.session?.proximitySession?.locale?.time?.start,
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.End,
      Header: 'End',
      accessor: (ae: ScheduleAttendanceEntry) =>
        ae.session?.proximitySession?.locale?.time?.end,
      disableSortBy: true,
    },
    {
      id: AttendanceColumnName.TotalHours,
      Header: 'Total Hours',
      accessor: (ae: ScheduleAttendanceEntry) =>
        ae.session?.proximitySession?.span?.hours?.toFixed(2) ?? '0',
      disableSortBy: true,
    },
  ]);

export const getFilterItems = ({
  attendanceStatuses,
}: {
  attendanceStatuses: string[];
}): FilterItems<ScheduleAttendanceFilters> => ({
  attendanceStatuses: {
    header: 'Status',
    type: 'dropdown',
    defaultValue: attendanceStatuses,
    componentProps: {
      options: attendanceStatusesOptions,
      isMulti: true,
      allowSearch: false,
    },
  },
});
